import React, { useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';
import OtpInput from 'react-otp-input';
import queryString from 'query-string';
import {
  BaseBox,
  FlexBox,
  Body1,
  Body2,
  Heading2,
  Heading4,
  PrimaryButton,
  Icons,
  message,
} from '@docon/docon-ui-library';
import SliderPane from './SliderPane';
import {
  login,
  verifyOtp,
  whatsAppOptIn,
  checkWhatsAppConsent,
  updateAuthState,
  Status,
} from 'redux/reducer/auth';
import Image from 'shared/Image';
import { cleverTapAnl } from 'utilities';
import firebaseService from 'app/firebaseSetup';
import { eventService, headerKey, restClient } from 'services';
import { getNextPathname } from 'utils';
import { borderColor, colors } from 'utilities/colorConfig';
import { EventLoginNumberChange, EventLoginOtpResend, EventLoginOtpSubmit, EventLoginSuccess } from 'utilities/clevertapEvent';

const doconLogo = '/images/docon.svg';

const Section = styled(FlexBox)`
  flex-direction: column;
  justify-content: space-between;
  width: 50%;
`;

const Title = styled(Heading2)`
  font-weight: 700;
  margin-bottom: 0;
`;

const SubTitle = styled(Heading4)`
  margin-top: 16px;
  color: ${colors.subHeading};
`;

const MobileTxt = styled(Heading4)`
  font-weight: 700;
  letter-spacing: 0.25px;
  margin-bottom: 20px;
  margin-top: 8px;

  .btnNum {
    font-weight: 500;
    padding-left: 6px;
    color: ${colors.primaryBlue};
    cursor: pointer;
  }
`;

const Timer = styled(Heading4)`
  margin: 12px 0 20px;
  display: flex;
  align-items: center;
  font-weight: 500;
  gap: 8px;
  color: ${colors.subHeading};
`;

const Form = styled(BaseBox)`
  position: relative;
  max-width: 325px;
  margin: auto;
  text-align: left;

  .inputStyle {
    width: 2.875rem !important;
    height: 2.875rem;
    padding: 0px;
    margin: 0 3.5px;
    font-size: 2rem;
    border-radius: 4px;
    border: 1px solid ${borderColor};
  }
`;

const ErrorText = styled(Body2)`
  position: absolute;
  width: 100%;
  color: ${colors.error};
  padding-top: 8px;
  font-weight: 500;
  text-align: center;
`;

const Root = styled(FlexBox)`
  position: relative;
  height: 100vh;
  text-align: center;
  
  ${() => isMobile && css`
    padding-top: 24px;
    height: 100%;
    flex-direction: column;
    flex-wrap: nowrap;

    ${Section} {
      width: 100%;
    }

    .doconLogo {
      height: 40px;
    }

    ${Icons.BackIcon} {
      position: absolute;
      left: 16px;
      margin: 0;
      height: 32px;
    }

    ${Form} {
      margin: 28px 20px 40px;
      max-width: 100%;

      ${Title} {
        font-size: 18px;
        line-height: 28px;
      }

      ${Heading4} {
        font-size: 14px;
        line-height: 16px;
      }
    }
  `}
`;

function VerifyOtp({
  history,
  onlyForm,
  mobile,
  formClassName,
  onNumberChange,
}: any) {
  const location = useLocation();
  const searchParams = queryString.parse(location.search);
  const mobileNumber = mobile || location.state;
  const {
    user,
    accessToken,
    loginError,
    otpVerifying,
    otpStatus,
    otpError,
    whatsAppConsentStatus,
  } = useSelector(state => state.auth);
  const dispatch = useDispatch();

  const [timer, setTimer] = useState(25);
  const timerInterval = useRef<any>();
  const [otp, setOtp] = useState('');

  useEffect(() => {
    eventService.startActionDuration('submit_otp');
    eventService.startActionDuration('resend_otp');

    if (!mobileNumber) {
      history.replace('/');
    }

    startTimer();
    return () => {
      clearInterval(timerInterval.current);
      dispatch(updateAuthState({ otpStatus: '', loginStatus: '', otpError: '' }));
    };
  }, []);

  useEffect(() => {
    if (accessToken) {
      if (otpStatus === Status.fulfilled) {
        // cleverTapAnl.configureProfile(
        //   user.userID,
        //   `${user.firstName || ''} ${user.lastName || ''}`,
        //   user.email,
        // );
        restClient.setHeader(headerKey.AccessToken, accessToken);
        dispatch(checkWhatsAppConsent(mobileNumber));
        setTimeout(() => {
          firebaseService.userID = user.userID;
          firebaseService.registerToken();
          triggerLoginSuccessEvent();
        }, 0);
      }

      history.replace({
        pathname: getNextPathname(searchParams),
        search: location.search,
      });
    }
  }, [accessToken]);

  useEffect(() => {
    if (whatsAppConsentStatus === 'RECORD_NOT_FOUND') {
      dispatch(whatsAppOptIn({
        'patientID': null,
        'doctorID': null,
        'countryCode': '91',
        'phoneNumber': mobileNumber,
        'source': 'MWEB',
        'platform': 'MOBILE',
        'createdBy': 'user',
      }));
    }
  }, [whatsAppConsentStatus]);

  useEffect(() => {
    if (loginError) {
      stopTimer();
      message.error(loginError);
    }
  }, [loginError]);

  useEffect(() => {
    if (!timer)
      clearInterval(timerInterval.current);
  }, [timer]);

  function startTimer() {
    setTimer(25);
    timerInterval.current = setInterval(() => {
      setTimer((time) => (time - 1));
    }, 1000);
  }

  function stopTimer() {
    setTimer(0);
  }

  function handleChange(value: any) {
    setOtp(value);
    if (loginError || otpError)
      dispatch(updateAuthState({ loginError: '', otpError: '' }));
  }

  function goBack() {
    triggerNumberChangeEvent();
    history.replace({
      pathname: '/',
      state: mobileNumber,
    });
  }

  function resendOtp() {
    triggerResendOtpEvent();
    startTimer();
    dispatch(login({ mobileNumber }));
  }

  function submitOtp() {
    triggerSubmitOtpEvent();
    dispatch(verifyOtp({
      mobileNumber,
      otp,
    }));
  }

  function triggerNumberChangeEvent() {
    cleverTapAnl.event({
      event_name: 'login_number_change',
      event: {
        number_length: 10,
        login_mode: 'otp',
        page_name: 'login_otp',
      },
    } as EventLoginNumberChange);
  }

  function triggerResendOtpEvent() {
    cleverTapAnl.event({
      event_name: 'login_otp_resend',
      event: {
        number_length: 10,
        login_mode: 'otp',
        page_name: 'login_otp',
        time_between_succ_otp: eventService.endActionDuration('resend_otp'),
      },
    } as EventLoginOtpResend);
  }

  function triggerSubmitOtpEvent() {
    cleverTapAnl.event({
      event_name: 'login_otp_submit',
      event: {
        page_name: 'login_otp',
        otp_length: otp.length,
        login_mode: 'otp',
        otp_submission_mode: 'manual',
        time_to_submit_otp: eventService.endActionDuration('submit_otp'),
      },
    } as EventLoginOtpSubmit);
  }

  function triggerLoginSuccessEvent() {
    cleverTapAnl.event({
      event_name: 'login_success',
      event: {
        page_name: 'home',
        login_mode: 'otp',
        time_to_login: eventService.endActionDuration('login_success'),
      },
    } as EventLoginSuccess);
  }

  function renderForm() {
    return (
      <Form className={formClassName}>
        <Title title='Enter OTP' />
        <SubTitle title='4 digit OTP has been sent to your mobile number' />

        <MobileTxt>
          <span>{mobileNumber} </span>
          <span className='btnNum' onClick={onNumberChange || goBack}>Change Number</span>
        </MobileTxt>

        <OtpInput
          numInputs={4}
          value={otp}
          inputStyle='inputStyle'
          isInputNum={true}
          onChange={handleChange}
        />

        <Timer>
          {
            timer === 0 ?
              <>
                <Body1 tagAs='span' title='Didn’t receive the code?' />
                <Body1
                  tagAs='span'
                  className='clickable'
                  fontWeight={500}
                  color={colors.primaryBlue}
                  title='Resend OTP'
                  onClick={resendOtp}
                />
              </>
              :
              <>
                <Icons.TimerIcon width={20} />
                Resend OTP in 00:{timer < 10 ? `0${timer}` : timer}
              </>
          }
        </Timer>

        <PrimaryButton
          isBlock
          title='Continue'
          icon={otpVerifying ? 'loading' : ''}
          disabled={otp?.length !== 4}
          onClick={submitOtp}
        />
        <ErrorText title={otpError || loginError} />
      </Form>
    );
  }

  if (onlyForm) return renderForm();

  return (
    <Root>
      {
        isMobile ? (
          <>
            <Icons.BackIcon hideBorder onClick={goBack} />
            <Image className='doconLogo' src={doconLogo} alt='Docon' />
          </>
        ) : <SliderPane />
      }

      <Section>
        {renderForm()}
      </Section>
    </Root>
  );
}

export default VerifyOtp;
